import { ToolResultOutput } from "@intelligems/ig-types/schemas/ai/nodes/tool-execution-result";
import { v4 } from "uuid";

import { logToolActivity } from "@src/features/ai/components/AiChat/utils/logToolActivity";
import type { AppListenerApi } from "@src/store/rootState";
import { handleAiChatAction } from "@src/store/slices/aiChat";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { fireVivTurn } from "@src/store/slices/experienceBuilder/ai/thunks/fireVivTurn";
import { getPageState } from "@src/store/slices/experienceBuilder/common/selectors/getPageState";

import { awaitTurnFinished } from "./awaitTurnFinished";
import { PAGE_READY_TIMEOUT } from "./constants";
import { DetectedContinuation, StreamPartAction } from "./types";

const { setContinuationInFlight } = experienceBuilderAiSlice.actions;

/** Inject a canned assistant reply (the `assistant`-kind continuation). */
function injectAssistantMessage(message: string, api: AppListenerApi) {
  const now = Date.now();
  api.dispatch(
    handleAiChatAction({
      type: "addMessage",
      turnId: v4(),
      nodeId: v4(),
      nodeName: "Continuation",
      message: {
        id: `assistant-${v4()}`,
        role: "assistant",
        timestamp: now,
        parts: [
          { id: `part-${v4()}`, type: "text", text: message, timestamp: now },
        ],
      },
    })
  );
}

/**
 * Wait for the preview iframe to reach `ready`. A follow-up turn captures page
 * HTML, so firing while the page is mid-load/error would let `requestPageHtml`
 * ship a stale cached snapshot. Returns true once ready, false on timeout —
 * the same defer-until-ready guard the chat input enforces for user sends.
 *
 * The current state is checked first. `api.condition` only evaluates its
 * predicate when a later action is dispatched; it never reads the state it
 * starts in. This wait is registered after the turn's `finish`, and the stream
 * often delivers `finish` and its trailing actions in one chunk, so by the time
 * it registers nothing more is dispatched while the builder sits idle. Without
 * this check the wait then timed out and the follow-up was dropped silently.
 */
async function waitForPageReady(api: AppListenerApi): Promise<boolean> {
  if (getPageState(api.getState())?.type === "ready") {
    return true;
  }
  return api.condition(
    (_, state) => getPageState(state)?.type === "ready",
    PAGE_READY_TIMEOUT
  );
}

/**
 * The full continuation sequence for one turn-ending tool: apply its side
 * effect, wait for it to settle, then once the stream's `finish` lands, fire
 * the follow-up. The terminal watch is registered *before* the side effect is
 * applied — sequencing on the explicit `finish` (not an observed pending flag)
 * is what makes the continuation reliable regardless of how long the server
 * takes to close the stream.
 */
export async function runContinuationSequence(
  { trigger, resolve }: DetectedContinuation,
  api: AppListenerApi
) {
  const { turnId } = trigger;
  const finished = awaitTurnFinished(api, turnId);

  api.dispatch(setContinuationInFlight(true));
  const continuation = await resolve(api);

  if (!(await finished)) {
    // Stopped / errored / timed out — drop the continuation.
    api.dispatch(setContinuationInFlight(false));
    return;
  }

  // Assistant replies inject immediately — no new turn, no page capture, so no
  // page-ready wait.
  if (continuation.kind === "assistant") {
    injectAssistantMessage(continuation.message, api);
    api.dispatch(setContinuationInFlight(false));
    return;
  }

  // A parked client tool resumes the suspended node in place: it answers the
  // open call and carries no fresh page HTML, so there's no page-ready guard.
  if (continuation.kind === "tool-result") {
    // Echo the result onto the already-streamed tool part: reuse the trigger's
    // envelope (same turn/node/message) and swap its `tool-input-available` for
    // a `tool-output-available`, which the reducer merges in place. This flips
    // the chat task to resolved without re-adding the message — the server
    // resume only updates the model's history. Same optimistic-update pattern
    // as the user echo in UiActionsChat. The result is produced client-side, so
    // it never flows through the SSE bridge; log it here too, so the dev
    // console renders the output (incl. any screenshot) just like streamed
    // tool output.
    const echo: StreamPartAction = {
      ...trigger,
      streamPart: {
        type: "tool-output-available",
        toolCallId: continuation.toolCallId,
        output: continuation.output as ToolResultOutput,
        timestamp: Date.now(),
        nodeId: trigger.nodeId,
        nodeType: trigger.nodeType,
        nodeName: trigger.nodeName,
      },
    };
    logToolActivity(echo);
    api.dispatch(handleAiChatAction(echo));
    api.dispatch(
      fireVivTurn({
        kind: "continuation-tool-result",
        toolCallId: continuation.toolCallId,
        output: continuation.output,
      })
    );
    return;
  }

  // A follow-up turn captures fresh page HTML, so wait for the iframe to be
  // ready before firing; abort (don't ship stale HTML) if it never is.
  if (!(await waitForPageReady(api))) {
    api.dispatch(setContinuationInFlight(false));
    return;
  }

  // `fireVivTurn`'s `beginTurn` clears `continuationInFlight`, handing
  // the busy lock off without a gap — so we deliberately don't clear it here.
  api.dispatch(
    fireVivTurn(
      continuation.kind === "auto-verify"
        ? { kind: "continuation-auto-verify" }
        : { kind: "continuation-user", message: continuation.message }
    )
  );
}
